import { Component, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'

import { Tilitapahtuma } from '@lemon/core-firebase/model/tiliote'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'

import { Observable, combineLatest, of } from 'rxjs'
import { map, startWith, switchMap, tap } from 'rxjs/operators'

import { Lasku } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'

interface ViitehakuLomake {
  viitenumero: string
}

@Component({
  templateUrl: './tilitapahtumien-kohdistusdebug.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilitapahtumienKohdistusdebugComponent implements OnInit {
  lomake: FieldTree<ViitehakuLomake>
  name = '' + Math.random()
  laskutObservable: Observable<Lasku[]>
  readonly laskutLataa = signal(true)
  tapahtumatObservable: Observable<Tilitapahtuma[]>
  readonly tapahtumatLataa = signal(true)
  private readonly _injector = inject(Injector)

  constructor(
    private _firebase: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _laskuUriService: LaskuUriService,
  ) {}

  ngOnInit() {
    this.lomake = form(
      signal<ViitehakuLomake>({ viitenumero: '' }),
      polku => {
        required(polku.viitenumero)
      },
      { injector: this._injector },
    )

    const viitenumeroObservable: Observable<string> = arvonMuutokset(this.lomake.viitenumero, { injector: this._injector }).pipe(startWith(''))

    this.laskutObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, viitenumeroObservable]).pipe(
      tap(() => {
        setTimeout(() => {
          this.laskutLataa.set(true)
        }, 10)
      }),
      switchMap(([asiakas, viitenumero]) => {
        if (asiakas && viitenumero && viitenumero.length > 3) {
          return this._firebase.firestoreCollection<Lasku>(this._laskuUriService.getCustomerLaskutCollectionUri(asiakas.avain)).where('viitenumero', '==', viitenumero).listen()
        }
        return of([])
      }),
      tap(() => {
        setTimeout(() => {
          this.laskutLataa.set(false)
        }, 10)
      }),
    )

    const tilitapahtumatViitteelleObservable: Observable<Tilitapahtuma[]> = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, viitenumeroObservable]).pipe(
      tap(() => this.tapahtumatLataa.set(true)),
      switchMap(([asiakas, viitenumero]) => {
        if (asiakas && viitenumero && viitenumero.length > 3) {
          return this._firebase
            .firestoreCollection<Tilitapahtuma>('customers/' + asiakas.avain + '/bank-transactions')
            .where('viite', '==', viitenumero)
            .listen()
        }
        return of<Tilitapahtuma[]>([])
      }),
    )

    const tilitapahtumatYliajoviitteelleObservable: Observable<Tilitapahtuma[]> = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, viitenumeroObservable]).pipe(
      tap(() => {
        setTimeout(() => {
          this.tapahtumatLataa.set(true)
        }, 10)
      }),
      switchMap(([asiakas, viitenumero]) => {
        if (asiakas && viitenumero && viitenumero.length > 3) {
          return this._firebase
            .firestoreCollection<Tilitapahtuma>('customers/' + asiakas.avain + '/bank-transactions')
            .where('yliajoviite', '==', viitenumero)
            .listen()
        }
        return of<Tilitapahtuma[]>([])
      }),
    )

    this.tapahtumatObservable = combineLatest([tilitapahtumatViitteelleObservable, tilitapahtumatYliajoviitteelleObservable]).pipe(
      map(([viitteelle, yliajoviitteelle]) => {
        const kaikki: Tilitapahtuma[] = []
        return kaikki.concat(viitteelle).concat(yliajoviitteelle)
      }),
      tap(() => {
        setTimeout(() => {
          this.tapahtumatLataa.set(false)
        }, 10)
      }),
    )
  }
}
